<template>
  <div class="history-card">
    <div class="history-header">
      <i class="uil-history"></i>
      <span>{{ $t("gift.history") }}</span>
    </div>

    <div v-if="giftHistories.length === 0 && !loading" class="history-empty">
      <i class="uil-inbox"></i>
      <span>{{ $t("gift.no_history") || "Henüz hediye alınmamış" }}</span>
    </div>

    <div v-else ref="historyContainer" class="history-container">
      <ul class="history-list">
        <li v-for="(item, index) in giftHistories" :key="'history_' + index" class="history-item">
          <div class="history-item-left">
            <img :src="item.image" :alt="item.name" class="history-image"/>
            <span class="history-name">{{ item.name }}</span>
          </div>
          <div class="history-item-right">
            <span class="history-point">{{ item.point }} <i class="uil-coins"></i></span>
            <span class="history-date">{{ item.date }}</span>
          </div>
        </li>
        <li v-if="loadingMore" class="history-loading">
          <i class="uil-spinner-alt"></i>
          <span>{{ $t("general.loading") || "Yükleniyor..." }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import _ from "lodash";

export default {
  name: "GiftHistoryCard",
  props: {
    giftHistories: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: true
    }
  },
  mounted() {
    this.debouncedScroll = _.debounce(this.handleScroll, 200);
    this.$nextTick(() => {
      const containerElement = this.$refs.historyContainer;
      if (containerElement) {
        containerElement.addEventListener('scroll', this.debouncedScroll);
      }
    });
  },
  beforeDestroy() {
    const containerElement = this.$refs.historyContainer;
    if (containerElement && this.debouncedScroll) {
      containerElement.removeEventListener('scroll', this.debouncedScroll);
      this.debouncedScroll.cancel();
    }
  },
  methods: {
    handleScroll(event) {
      const list = event.target;
      const scrollTop = list.scrollTop;
      const scrollHeight = list.scrollHeight;
      const clientHeight = list.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight * 0.9 && this.hasMore && !this.loadingMore) {
        this.$emit('load-more');
      }
    }
  }
}
</script>

<style scoped>
.history-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  min-height: 300px;
  max-height: 600px;
}

.history-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 200px;
}

.history-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 12px;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
}

.history-header i {
  color: var(--backgroundColor, #6366f1);
  font-size: 1.1rem;
}

.history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 0;
  color: #9ca3af;
}

.history-empty i {
  font-size: 2rem;
}

.history-empty span {
  font-size: 0.85rem;
}

.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  background: #f9fafb;
  border-radius: 8px;
  transition: background 0.2s;
}

.history-item:hover {
  background: #f3f4f6;
}

.history-item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.history-image {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}

.history-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-item-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
  margin-left: 10px;
}

.history-point {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--backgroundColor, #6366f1);
  display: flex;
  align-items: center;
  gap: 4px;
}

.history-point i {
  color: #f59e0b;
  font-size: 0.85rem;
}

.history-date {
  font-size: 0.7rem;
  color: #9ca3af;
}

.history-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #9ca3af;
  font-size: 0.85rem;
  gap: 8px;
}

.history-loading i {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .history-card {
    padding: 14px;
  }

  .history-header {
    font-size: 0.9rem;
  }
}

@media (max-width: 768px) {
  .history-card {
    padding: 12px;
  }

  .history-header {
    font-size: 0.85rem;
    padding-bottom: 10px;
    margin-bottom: 10px;
  }

  .history-list {
    max-height: 300px;
    gap: 8px;
  }

  .history-item {
    padding: 8px;
  }

  .history-image {
    width: 32px;
    height: 32px;
  }

  .history-name {
    font-size: 0.75rem;
  }

  .history-point {
    font-size: 0.7rem;
  }

  .history-date {
    font-size: 0.6rem;
  }
}

@media (max-width: 576px) {
  .history-card {
    padding: 10px;
    border-radius: 10px;
  }

  .history-header {
    font-size: 0.8rem;
    padding-bottom: 8px;
    margin-bottom: 8px;
    gap: 6px;
  }

  .history-header i {
    font-size: 1rem;
  }

  .history-empty {
    padding: 16px 0;
  }

  .history-empty i {
    font-size: 1.5rem;
  }

  .history-empty span {
    font-size: 0.75rem;
  }

  .history-list {
    max-height: 300px;
    gap: 6px;
  }

  .history-item {
    padding: 8px;
    border-radius: 6px;
  }

  .history-item-left {
    gap: 8px;
  }

  .history-image {
    width: 36px;
    height: 36px;
    border-radius: 4px;
  }

  .history-name {
    font-size: 0.7rem;
  }

  .history-item-right {
    margin-left: 8px;
  }

  .history-point {
    font-size: 0.65rem;
  }

  .history-date {
    font-size: 0.55rem;
  }
}

@media (max-width: 400px) {
  .history-card {
    padding: 8px;
  }

  .history-header {
    font-size: 0.75rem;
  }

  .history-image {
    width: 30px;
    height: 30px;
  }

  .history-name {
    font-size: 0.65rem;
  }
}

@media (max-width: 576px) {
  .history-card {
    width: 100%;
    box-sizing: border-box;
    min-height: 0;
    max-height: 420px;
    padding: 16px;
    border-radius: 12px;
  }

  .history-header {
    font-size: 16px;
    line-height: 1.5;
  }

  .history-item {
    width: 100%;
    min-height: 56px;
    box-sizing: border-box;
    padding: 12px;
    gap: 8px;
    align-items: flex-start;
  }

  .history-item-left {
    flex: 1 1 auto;
    min-width: 0;
  }

  .history-image {
    width: 40px;
    height: 40px;
  }

  .history-name {
    min-width: 0;
    font-size: 14px;
    line-height: 1.5;
  }

  .history-item-right {
    width: 88px;
    max-width: 35%;
    min-width: 0;
    margin-left: 8px;
  }

  .history-point,
  .history-date {
    max-width: 100%;
    white-space: normal;
    text-align: right;
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
